import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import { useNotifications } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useWindowSize } from '../hooks/useWindowSize';

const NotificationBell = () => {
  const { 
    unreadCount, 
    isLoading: contextLoading, 
    startPolling, 
    stopPolling, 
    decrementUnreadCount,
    updateUnreadCount 
  } = useNotifications();
  
  const { isDarkMode } = useTheme();
  const { width } = useWindowSize();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Start polling when component mounts
    startPolling();
    
    // Cleanup when component unmounts
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);



  const handleCloseDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleCloseDropdown();
      }
    };

    // Only add click outside listener on desktop
    if (width >= 768) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [width, handleCloseDropdown]);

  const loadNotifications = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await apiService.getNotifications({ limit: 10 });
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      loadNotifications();
    }
  };



  const handleMarkAsRead = async (notificationId) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      // Update context state
      decrementUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      // Update context state
      updateUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'transfer_received': return '💰';
      case 'transfer_sent': return '📤';
      case 'internal_transfer': return '🔄';
      case 'fee_charged': return '💳';
      case 'account_created': return '🏦';
      default: return '📢';
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={handleBellClick}
        data-testid="notification-bell"
        style={{
          position: 'relative',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
          fontSize: '1.5rem'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
      >
        🔔
        {unreadCount > 0 && (
          <span 
            data-testid="unread-count"
            style={{
              position: 'absolute',
              top: '0.25rem',
              right: '0.25rem',
              backgroundColor: '#dc3545',
              color: 'white',
              borderRadius: '50%',
              width: '1.2rem',
              height: '1.2rem',
              fontSize: '0.7rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          {/* Mobile backdrop */}
          {width < 768 && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 999
              }}
              onClick={handleCloseDropdown}
            />
          )}
          
          <div 
            data-testid="notifications-dropdown"
            style={{
              position: width < 768 ? 'fixed' : 'absolute',
              top: width < 768 ? '50%' : '100%',
              left: width < 768 ? '50%' : 'auto',
              right: width < 768 ? 'auto' : 0,
              transform: width < 768 ? 'translate(-50%, -50%)' : 'none',
              width: width < 768 ? 'min(350px, 90vw)' : '350px',
              backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
              border: isDarkMode ? '1px solid #404040' : '1px solid #ddd',
              borderRadius: width < 768 ? '16px' : '8px',
              boxShadow: width < 768 ? (isDarkMode ? '0 20px 40px rgba(0,0,0,0.5)' : '0 20px 40px rgba(0,0,0,0.25)') : (isDarkMode ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)'),
              zIndex: 1000,
              maxHeight: width < 768 ? '80vh' : '400px',
              overflow: 'hidden',
              animation: width < 768 ? 'fadeInScale 0.2s ease-out' : 'none'
            }}>
          {/* Header */}
          <div style={{
            padding: width < 768 ? '0.75rem' : '1rem',
            borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: width < 768 ? '0.9rem' : '1rem', 
              color: isDarkMode ? '#ffffff' : '#333' 
            }}>
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                data-testid="mark-all-read"
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: isDarkMode ? '#4dabf7' : '#007bff',
                  cursor: 'pointer',
                  fontSize: width < 768 ? '0.75rem' : '0.8rem'
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div 
            data-testid="notifications-list"
            style={{ 
              maxHeight: width < 768 ? '50vh' : '300px', 
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}>
            {loading ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: isDarkMode ? '#b3b3b3' : '#666' }}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: isDarkMode ? '#b3b3b3' : '#666' }}>
                No notifications
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  data-testid={`notification-${notification.id}`}
                  style={{
                    padding: width < 768 ? '0.75rem' : '1rem',
                    borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #eee',
                    backgroundColor: notification.read ? 'transparent' : (isDarkMode ? '#1e1e1e' : '#f8f9fa'),
                    cursor: 'pointer'
                  }}
                  onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: notification.read ? 'normal' : 'bold',
                        color: isDarkMode ? '#ffffff' : '#333',
                        fontSize: '0.9rem',
                        marginBottom: '0.25rem'
                      }}>
                        {notification.title}
                      </div>
                      <div style={{
                        color: isDarkMode ? '#b3b3b3' : '#666',
                        fontSize: '0.8rem',
                        lineHeight: '1.3',
                        marginBottom: '0.25rem'
                      }}>
                        {notification.message}
                      </div>
                      <div style={{
                        color: isDarkMode ? '#888888' : '#999',
                        fontSize: '0.7rem'
                      }}>
                        {formatTime(notification.created_at)}
                      </div>
                    </div>
                    {!notification.read && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#4dabf7' : '#007bff',
                        borderRadius: '50%',
                        flexShrink: 0,
                        marginTop: '0.25rem'
                      }} />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{
              padding: width < 768 ? '0.5rem' : '0.75rem',
              borderTop: isDarkMode ? '1px solid #404040' : '1px solid #eee',
              textAlign: 'center'
            }}>
              <button
                onClick={() => {
                  handleCloseDropdown();
                  // Navigate to notifications page by setting URL
                  window.location.hash = '#notifications';
                  // Or trigger navigation in parent component
                  window.dispatchEvent(new CustomEvent('navigate-to-notifications'));
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: isDarkMode ? '#4dabf7' : '#007bff',
                  cursor: 'pointer',
                  fontSize: width < 768 ? '0.8rem' : '0.9rem'
                }}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell; 